import type { ComponentType, SVGProps } from "react"
import { IconStorageFill18 } from "nucleo-ui-fill-18"

import {
	AttioIcon,
	HubspotIcon,
	IntercomIcon,
	LinearIcon,
	PlainLogo,
	SlackIcon,
	ZapierIcon,
} from "@productlane/ui"
import { CursorIcon } from "@productlane/ui/src/icons/project/cursor-icon"
import { GitHubIcon } from "@productlane/ui/src/icons/project/github-icon"
import { TeamsIcon } from "@productlane/ui/src/icons/project/ms-teams-icon"
import { SentryIcon } from "@productlane/ui/src/icons/project/sentry-icon"

import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { useZeroWorkspace } from "@/lib/zero"

export type IntegrationKey =
	| "linear"
	| "slack"
	| "teams"
	| "github"
	| "zapier"
	| "plain"
	| "hubspot"
	| "attio"
	| "postgres"
	| "intercom"
	| "cursor"
	| "sentry"

export interface Integration {
	key: IntegrationKey
	title: string
	description: string
	href: string
	Icon: ComponentType<SVGProps<SVGSVGElement>>
	adminOnly?: boolean
	connectedOnly?: boolean
	/** Shown only where the server reports it available (our own workspace). */
	internalOnly?: boolean
	keywords: string
}

export const INTEGRATIONS: Integration[] = [
	{
		key: "linear",
		title: "Linear",
		description: "Sync Linear projects, issues and customers.",
		href: "/settings/integrations/linear",
		Icon: LinearIcon,
		keywords: "linear issues sync connect",
	},
	{
		key: "slack",
		title: "Slack",
		description: "Sync conversations from Slack.",
		href: "/settings/integrations/slack",
		Icon: SlackIcon,
		keywords: "slack channel notifications connect",
	},
	{
		key: "teams",
		title: "Teams",
		description: "Sync conversations from Microsoft Teams.",
		href: "/settings/integrations/teams",
		Icon: TeamsIcon,
		keywords: "microsoft teams notifications",
	},
	{
		key: "github",
		title: "GitHub",
		description:
			"Link a repository so the Productlane Agent and AI drafts can read PRs and source code.",
		href: "/settings/integrations/github",
		Icon: GitHubIcon,
		keywords: "github repository commits docs proposals",
	},
	{
		key: "zapier",
		title: "Zapier",
		description: "Connect countless tools with Productlane via Zapier.",
		href: "/settings/integrations/zapier",
		Icon: ZapierIcon,
		keywords: "zapier automation workflow",
	},
	{
		key: "plain",
		title: "Plain",
		description:
			"Push conversations to Productlane and view related projects in Plain.",
		href: "/settings/integrations/plain",
		Icon: PlainLogo,
		connectedOnly: true,
		keywords: "plain customer data",
	},
	{
		key: "hubspot",
		title: "Hubspot",
		description:
			"Log daily support notes, push contacts and companies, and sync segments.",
		href: "/settings/integrations/hubspot",
		Icon: HubspotIcon,
		keywords: "hubspot crm sync notes contacts companies push",
	},
	{
		key: "attio",
		title: "Attio",
		description:
			"Log daily support activity to the matching person and company in Attio.",
		href: "/settings/integrations/attio",
		Icon: AttioIcon,
		keywords: "attio crm sync",
	},
	{
		key: "postgres",
		title: "PostgreSQL",
		description:
			"Give the sidebar AI assistant read access to a PostgreSQL database.",
		href: "/settings/integrations/postgres",
		Icon: IconStorageFill18,
		adminOnly: true,
		keywords: "postgresql database connection sql data access",
	},
	{
		key: "intercom",
		title: "Intercom migrator",
		description:
			"Move your conversations, contacts and help center from Intercom to Productlane.",
		href: "/settings/integrations/intercom",
		Icon: IntercomIcon,
		keywords: "intercom migrate migration import customer data",
	},
	{
		key: "cursor",
		title: "Cursor",
		description:
			"Connect Cursor Cloud to automatically resolve issues with coding agents.",
		href: "/settings/integrations/cursor",
		Icon: CursorIcon,
		connectedOnly: true,
		keywords: "cursor coding agent code generation",
	},
	{
		key: "sentry",
		title: "Sentry",
		description:
			"Connect a Sentry organization and choose which projects Productlane can read.",
		href: "/settings/integrations/sentry",
		Icon: SentryIcon,
		adminOnly: true,
		internalOnly: true,
		keywords: "sentry errors issues monitoring",
	},
]

/** Whether this workspace may use the Sentry integration; the server decides. */
export function useSentryStatus() {
	const { role } = useRole()
	return api.sentryIntegration.status.useQuery(undefined, {
		enabled: role === "ADMIN",
		staleTime: 60_000,
	})
}

export function useSentryAvailable(): boolean {
	return !!useSentryStatus().data?.available
}

// Connection state is spread across Zero relations and a few API endpoints, so
// this hook centralizes the lookups the listing and general pages both need.
export function useIntegrationConnections(): Record<IntegrationKey, boolean> {
	const workspace = useZeroWorkspace()
	const infoQuery = api.workspace.info.useQuery(undefined, {
		staleTime: 60_000,
	})
	const slackQuery = api.workspace.slackSettings.useQuery(undefined, {
		staleTime: 60_000,
	})
	const postgresQuery = api.postgresIntegration.status.useQuery(undefined, {
		staleTime: 60_000,
	})
	const sentryQuery = useSentryStatus()

	const info = infoQuery.data

	return {
		linear: !!workspace?.linearOrganizationId,
		slack: !!slackQuery.data?.connected,
		teams: !!workspace?.teamsSettings,
		github: !!workspace?.githubSettings,
		zapier: false,
		plain: !!workspace?.plainSettings,
		hubspot: !!info?.hubspotConnected,
		attio: !!info?.attioConnected,
		postgres: !!postgresQuery.data?.settings,
		intercom: !!workspace?.intercomSettings,
		cursor: !!info?.cursorConnected,
		sentry: !!sentryQuery.data?.settings,
	}
}

export const RECONNECT_TOOLTIP = "Reconnect to keep it running"

export function useIntegrationReconnects(): Partial<
	Record<IntegrationKey, boolean>
> {
	const workspace = useZeroWorkspace()
	const infoQuery = api.workspace.info.useQuery(undefined, {
		staleTime: 60_000,
	})
	return {
		linear: !!workspace?.linearTokenRevoked,
		hubspot: !!infoQuery.data?.hubspotReconnectRequired,
		slack: !!infoQuery.data?.slackReconnectRequired,
		attio: !!infoQuery.data?.attioReconnectRequired,
	}
}

export function visibleIntegrations(
	integrations: Integration[],
	options: {
		isAdmin: boolean
		connections: Record<IntegrationKey, boolean>
		internalAvailable?: boolean
	},
): Integration[] {
	return integrations.filter((integration) => {
		if (integration.internalOnly && !options.internalAvailable) return false
		if (integration.adminOnly && !options.isAdmin) return false
		if (integration.connectedOnly && !options.connections[integration.key])
			return false
		return true
	})
}

export function useVisibleIntegrations(): Integration[] {
	const { role } = useRole()
	const connections = useIntegrationConnections()
	const internalAvailable = useSentryAvailable()
	return visibleIntegrations(INTEGRATIONS, {
		isAdmin: role === "ADMIN",
		connections,
		internalAvailable,
	})
}
